import type { QueryClient } from "react-query";
import { NucToast } from "src/reusable/components/Toast";
import { getPartsAIRunDataByDialogueId } from "../services/PartsAIRunService";

/**
 * Each dialogue kind's own product. Run still shares "parts-ai" with the
 * dead comparison endpoint's legacy rows.
 */
export const PARTS_AI_PRODUCT_BY_KIND: Record<
  "run" | "search" | "comparison",
  string
> = {
  run: "parts-ai",
  search: "parts-sourcing",
  comparison: "item-equivalency",
};

// Listing keys are comma-joined product unions for reading a list only —
// never for stamping a new dialogue. Search and comparison listings pair these
// with a `kind` filter, which keeps the shared "parts-ai" slug from mixing
// kinds.
const withLegacyPartsAI = (slug: string) => `parts-ai,${slug}`;

/** Spans pre-split "parts-ai" search history too. */
export const PARTS_AI_SEARCH_LISTING_PRODUCT_KEY = withLegacyPartsAI(
  PARTS_AI_PRODUCT_BY_KIND.search,
);

/**
 * Spans legacy "parts-ai" comparison rows too. The backend classifies every
 * item-equivalency dialogue as kind "comparison".
 */
export const PARTS_AI_COMPARISON_LISTING_PRODUCT_KEY = withLegacyPartsAI(
  PARTS_AI_PRODUCT_BY_KIND.comparison,
);

/** Where a dialogue that cannot be opened sends the user. */
export const PARTS_AI_HOME_PATH = "/parts";

/**
 * Shown with the redirect to PARTS_AI_HOME_PATH; names the destination and
 * nothing about the cause, since a failed lookup and a missing run reach the
 * same branch.
 */
export const PARTS_AI_UNOPENABLE_DIALOGUE_MESSAGE =
  "Couldn't open this conversation. Redirecting to Parts AI home.";

export type PartsAIListingKind = "all" | "run" | "search" | "comparison";

/** Product filter behind each Parts AI recents tab. */
export const PARTS_AI_LISTING_PRODUCT_KEY_BY_KIND: Record<
  PartsAIListingKind,
  string
> = {
  // Spelled out so reordering PARTS_AI_PRODUCT_BY_KIND can't change the
  // query string, react-query cache key, or chat URL registry key.
  all: [
    PARTS_AI_PRODUCT_BY_KIND.run,
    PARTS_AI_PRODUCT_BY_KIND.search,
    PARTS_AI_PRODUCT_BY_KIND.comparison,
  ].join(","),
  run: PARTS_AI_PRODUCT_BY_KIND.run,
  search: PARTS_AI_SEARCH_LISTING_PRODUCT_KEY,
  comparison: PARTS_AI_COMPARISON_LISTING_PRODUCT_KEY,
};

/**
 * Resolves the chat URL for a Parts AI dialogue given its kind. Run dialogues
 * need a parts_ai_run_id lookup before the URL can be built; search and
 * comparison dialogues route directly. When the lookup fails the user is told
 * with a toast and sent to Parts AI home, so the message and the destination
 * never disagree.
 */
export const buildPartsAIChatUrl = async (
  dialogueId: number,
  kind: string | null | undefined,
  queryClient: QueryClient,
): Promise<string> => {
  if (kind === "search") {
    return `/parts/chat/search/${dialogueId}`;
  }
  if (kind === "comparison") {
    // Comparison chats are Item Equivalency evaluations; the old
    // /parts/chat/comparison path stays routable as an alias for old links.
    return `/item-equivalency/chat/${dialogueId}`;
  }
  // Default ("run"): fetch the run id from the dialogue, then build the run URL.
  try {
    const run = await queryClient.fetchQuery(
      ["partsAIDialogue", dialogueId],
      () => getPartsAIRunDataByDialogueId(dialogueId),
      { staleTime: 5 * 60 * 1000 }, // Cache for 5 minutes
    );
    if (run.id) {
      return `/parts/chat/${run.id}/${dialogueId}`;
    }
  } catch (error) {
    console.error("PartsAI: Failed to fetch run ID:", error);
  }
  NucToast({
    message: PARTS_AI_UNOPENABLE_DIALOGUE_MESSAGE,
    type: "warning",
  });
  return PARTS_AI_HOME_PATH;
};
